import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { App as AntApp, ConfigProvider } from 'antd';
import viVN from 'antd/locale/vi_VN';
import { useLayoutEffect, useMemo } from 'react';
import { useAuthSync } from './features/authentication/presentation/hooks/useAuth';
import { AppStateProvider } from './core/app_state/AppStateContext';
import { AclProvider } from './core/acl/AclContext';
import { AclDevToolsProvider, ACL_DEVTOOLS_ENABLED } from './core/acl/AclDevToolsContext';
import { AclDevToolsPanel } from './core/acl/AclDevToolsPanel';
import { ThemeProvider, useTheme } from './core/theme/ThemeContext';
import { buildAntTheme } from './core/theme/palettes';
import App from './App.jsx';
import { appFeedback, registerAppFeedback } from './shared/feedback/appFeedback';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function AuthSyncBridge() {
  useAuthSync();
  return null;
}

function FeedbackBridge() {
  const apis = AntApp.useApp();

  useLayoutEffect(() => {
    const unregister = registerAppFeedback(apis);
    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action?.type === 'error') {
        const error = event.action.error;
        appFeedback.error(error, { description: error?.message ?? 'Failed to load data' });
      }
    });
    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action?.type === 'error') {
        if (event.mutation.options.onError) return;
        const error = event.action.error;
        appFeedback.error(error, { description: error?.message ?? 'Có lỗi xảy ra' });
      }
    });

    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
      unregister();
    };
  }, [apis]);

  return null;
}

function ThemedApp() {
  const { isDark, themeKey } = useTheme();
  const theme = useMemo(() => buildAntTheme(themeKey, isDark), [isDark, themeKey]);
  return (
    <ConfigProvider locale={viVN} theme={theme}>
      <AntApp notification={{ placement: 'bottomLeft' }}>
        <FeedbackBridge />
        <App />
        {ACL_DEVTOOLS_ENABLED && <AclDevToolsPanel />}
      </AntApp>
    </ConfigProvider>
  );
}

export function AppProviders() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthSyncBridge />
      <ThemeProvider>
        <AppStateProvider>
          <AclProvider>
            <AclDevToolsProvider>
              <ThemedApp />
            </AclDevToolsProvider>
          </AclProvider>
        </AppStateProvider>
      </ThemeProvider>
      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      )}
    </QueryClientProvider>
  );
}
